import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { studyFormsAPI, activityAPI, usefulDocsAPI, clientDocsAPI, officialRequestsAPI } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    Wrench, AlertTriangle, ExternalLink, Globe, FileText, IdCard,
    ClipboardList, Search, Loader2, ChevronRight, History, UserPlus, Copy,
    ClipboardCheck, Megaphone, Download, FolderOpen, Upload, Trash2, Edit, Tag, Eye,
    Network, Sparkles, Maximize2, Mail, ArrowLeft, BookOpen, Rocket, ChevronDown, ChevronUp,
    Presentation, Handshake,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import PdfAllPagesViewer from '@/components/PdfAllPagesViewer';
import AnnouncementsCatalog from '@/components/outils/AnnouncementsCatalog';
import { useObservedUsefulDocuments } from '@/hooks/useObservedUsefulDocuments';
import { publishUsefulDocsChange, USEFUL_DOCS_CHANGED, USEFUL_DOCS_STORAGE } from '@/lib/usefulDocumentEvents';

const VALNEXO_SITE = 'https://valnexo.fr';
const VALNEXO_PLAQUETTE = 'https://valnexo.fr/presentation-valnexo';

// Secteur réservé pour les 2 documents piliers de la nouvelle vision Valnexo :
// Fil rouge du discours + Qui cibler. Ils sont uploadés comme n'importe quel
// document utile (par un admin) mais épinglés en haut de page via ce marqueur.
const FONDATIONS_SECTEUR = 'Fondations Valnexo';
const FIL_ROUGE_TITLE_HINT = 'fil rouge';
const CIBLAGE_TITLE_HINT = 'cibler';

const JOB_ASSET_BASE = 'https://customer-assets.emergentagent.com/job_ae6c2612-50c6-4802-af4d-e4ae8c50a17e/artifacts';
const ORGANIGRAMME_URL = `${JOB_ASSET_BASE}/9ukuozq7_Organigramme%20valnexo.PNG`;
const MARKETING_ASSETS = [
    {
        id: 'recrutement',
        title: 'Recrutement',
        description: "Story à diffuser sur vos réseaux pour attirer et recruter de nouveaux apporteurs.",
        url: `${JOB_ASSET_BASE}/r83898zv_storie%20recrutement.jpeg`,
        filename: 'valnexo-story-recrutement.jpeg',
        adminOnly: true,
    },
    {
        id: 'cee',
        title: 'CEE',
        description: "Story dédiée aux économies d'énergie (CEE), prête à publier auprès de votre audience.",
        url: `${JOB_ASSET_BASE}/vueacw8i_storie%20CEE.jpeg`,
        filename: 'valnexo-story-cee.jpeg',
        adminOnly: false,
    },
    {
        id: 'courtage',
        title: 'Courtage',
        description: "Story courtage à partager sur vos réseaux pour valoriser votre offre.",
        url: `${JOB_ASSET_BASE}/y3xbtk8k_storie%20courtage%202.jpeg`,
        filename: 'valnexo-story-courtage.jpeg',
        adminOnly: false,
    },
];

export default function OutilsLiensUtilesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showProspectPicker, setShowProspectPicker] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [prospectQuery, setProspectQuery] = useState('');
    const [openingForm, setOpeningForm] = useState(false);
    const [businessCard, setBusinessCard] = useState(null);
    const [loadingCard, setLoadingCard] = useState(true);

    // Documents utiles à vos prospections
    const [usefulDocs, setUsefulDocs] = useState([]);
    // Section repliable/dépliable : reste FERMÉE par défaut pour ne pas monopoliser l'onglet.
    // L'utilisateur clique sur l'en-tête (ou le bouton "Explorer") pour la déplier.
    const [usefulDocsOpen, setUsefulDocsOpen] = useState(false);
    const [loadingDocs, setLoadingDocs] = useState(true);
    const [docTitle, setDocTitle] = useState('');
    const [docSecteur, setDocSecteur] = useState('');
    const [docFile, setDocFile] = useState(null);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [downloadingDocId, setDownloadingDocId] = useState(null);
    const [viewingDocId, setViewingDocId] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [docSearch, setDocSearch] = useState('');
    // Édition d'un document existant (admin uniquement)
    const [editingDoc, setEditingDoc] = useState(null); // objet doc en cours d'édition, ou null
    const [editTitle, setEditTitle] = useState('');
    const [editSecteur, setEditSecteur] = useState('');
    const [editFile, setEditFile] = useState(null); // fichier de remplacement (facultatif)
    const [savingEdit, setSavingEdit] = useState(false);
    const [showOrgaLightbox, setShowOrgaLightbox] = useState(false);
    const [orgaConfig, setOrgaConfig] = useState(null);
    const [showOfficialRequestGrid, setShowOfficialRequestGrid] = useState(false);

    // 📁 Documents de présentation client — miroir des documents utiles (fév 2026)
    // Mêmes règles admin/visibilité, collection séparée, même UX visionneuse/DL/edit/delete.
    const [clientDocs, setClientDocs] = useState([]);
    const [clientDocsOpen, setClientDocsOpen] = useState(false);
    const [loadingClientDocs, setLoadingClientDocs] = useState(true);
    const [clientDocTitle, setClientDocTitle] = useState('');
    const [clientDocSecteur, setClientDocSecteur] = useState('');
    const [clientDocFile, setClientDocFile] = useState(null);
    const [uploadingClientDoc, setUploadingClientDoc] = useState(false);
    const [downloadingClientDocId, setDownloadingClientDocId] = useState(null);
    const [viewingClientDocId, setViewingClientDocId] = useState(null);
    const [clientDocSearch, setClientDocSearch] = useState('');
    const [editingClientDoc, setEditingClientDoc] = useState(null);
    const [editClientTitle, setEditClientTitle] = useState('');
    const [editClientSecteur, setEditClientSecteur] = useState('');
    const [editClientFile, setEditClientFile] = useState(null);
    const [savingClientEdit, setSavingClientEdit] = useState(false);

    const docsRequest = useRef(0);
    const onDocumentsSeen = useCallback((versions) => {
        setUsefulDocs(previous => previous.map(doc => versions[doc.id] >= doc.revision ? { ...doc, is_unread: false } : doc));
    }, []);
    const docReading = useObservedUsefulDocuments({ userId: user?.id, documents: usefulDocs,
        renderKey: `${usefulDocsOpen}:${docSearch}`, onSeen: onDocumentsSeen });

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isApporteur = user?.role === 'apporteur';
    const isVP = user?.role === 'vp';
    const isParrain = user?.role === 'parrain';
    const ONBOARDING_LINK = 'https://valnexo.fr/onboarding';
    const REJOINDRE_LINK = 'https://valnexo.fr/rejoindre';

    useEffect(() => {
        loadBusinessCard();
        loadUsefulDocs();
        loadClientDocs();
        // Charger la config des demandes officielles (secteurs → dirigeants) pour VP/Admin
        if (user?.role === 'vp' || user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin) {
            officialRequestsAPI.config()
                .then((res) => setOrgaConfig(res.data))
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUsefulDocs = useCallback(async () => {
        const request = ++docsRequest.current;
        setLoadingDocs(true);
        try {
            const res = await usefulDocsAPI.list();
            if (request !== docsRequest.current) return;
            setUsefulDocs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Erreur chargement documents utiles', err);
        } finally {
            if (request === docsRequest.current) setLoadingDocs(false);
        }
    }, []);

    useEffect(() => {
        const refresh = () => { if (!document.hidden) loadUsefulDocs(); };
        const onStorage = event => { if (event.key === USEFUL_DOCS_STORAGE) refresh(); };
        window.addEventListener(USEFUL_DOCS_CHANGED, refresh);
        window.addEventListener('storage', onStorage);
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        const interval = setInterval(refresh, 60000);
        return () => {
            ++docsRequest.current; clearInterval(interval);
            window.removeEventListener(USEFUL_DOCS_CHANGED, refresh);
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [loadUsefulDocs]);

    const handleUploadDoc = async () => {
        if (!docTitle.trim() || !docSecteur.trim() || !docFile) {
            toast.error('Titre, secteur et fichier sont obligatoires');
            return;
        }
        setUploadingDoc(true);
        try {
            const fd = new FormData();
            fd.append('file', docFile);
            fd.append('title', docTitle.trim());
            fd.append('secteur', docSecteur.trim());
            await usefulDocsAPI.create(fd);
            publishUsefulDocsChange(user.id);
            toast.success('Document ajouté');
            setDocTitle(''); setDocSecteur(''); setDocFile(null);
            const input = document.getElementById('useful-doc-file');
            if (input) input.value = '';
            loadUsefulDocs();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'ajout");
        } finally {
            setUploadingDoc(false);
        }
    };

    const handleDownloadDoc = async (doc) => {
        setDownloadingDocId(doc.id);
        try {
            const res = await usefulDocsAPI.download(doc.id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.original_filename || doc.title || 'document';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            docReading.markSeen(doc);
        } catch {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setDownloadingDocId(null);
        }
    };

    const handleViewDoc = async (doc) => {
        setViewingDocId(doc.id);
        try {
            const res = await usefulDocsAPI.download(doc.id);
            const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            setPreviewDoc(doc);
            setPreviewUrl(url);
            docReading.markSeen(doc);
        } catch {
            toast.error("Erreur lors de l'ouverture du document");
        } finally {
            setViewingDocId(null);
        }
    };

    const closePreview = () => {
        if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewDoc(null);
    };

    const handleDeleteDoc = async (doc) => {
        if (!window.confirm(`Supprimer « ${doc.title} » ?`)) return;
        try {
            await usefulDocsAPI.remove(doc.id);
            toast.success('Document supprimé');
            setUsefulDocs((prev) => prev.filter((d) => d.id !== doc.id));
            publishUsefulDocsChange(user.id);
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openEditDoc = (doc) => {
        setEditingDoc(doc);
        setEditTitle(doc.title || '');
        setEditSecteur(doc.secteur || '');
        setEditFile(null);
    };
    const closeEditDoc = () => {
        setEditingDoc(null);
        setEditTitle('');
        setEditSecteur('');
        setEditFile(null);
    };
    const handleSaveEditDoc = async () => {
        if (!editingDoc) return;
        const title = editTitle.trim();
        const secteur = editSecteur.trim();
        if (!title || !secteur) {
            toast.error('Le titre et le secteur sont obligatoires');
            return;
        }
        setSavingEdit(true);
        try {
            const fd = new FormData();
            if (title !== editingDoc.title) fd.append('title', title);
            if (secteur !== editingDoc.secteur) fd.append('secteur', secteur);
            if (editFile) fd.append('file', editFile);
            if (![...fd.keys()].length) {
                toast.info('Aucun changement à enregistrer');
                setSavingEdit(false);
                return;
            }
            const res = await usefulDocsAPI.update(editingDoc.id, fd);
            const updated = res.data;
            setUsefulDocs((prev) => prev.map((d) => (d.id === editingDoc.id ? { ...d, ...updated } : d)));
            publishUsefulDocsChange(user.id);
            toast.success('Document mis à jour');
            closeEditDoc();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSavingEdit(false);
        }
    };

    const loadBusinessCard = async () => {
        setLoadingCard(true);
        try {
            const res = await api.get('/tools/my-business-card');
            setBusinessCard(res.data);
        } catch (err) {
            console.error('Erreur chargement carte de visite', err);
        } finally {
            setLoadingCard(false);
        }
    };

    // ─── Documents de présentation CLIENT (miroir de useful-documents) ───
    const loadClientDocs = async () => {
        setLoadingClientDocs(true);
        try {
            const res = await clientDocsAPI.list();
            setClientDocs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Erreur chargement documents client', err);
        } finally {
            setLoadingClientDocs(false);
        }
    };

    const handleUploadClientDoc = async () => {
        if (!clientDocTitle.trim() || !clientDocSecteur.trim() || !clientDocFile) {
            toast.error('Titre, secteur et fichier sont obligatoires');
            return;
        }
        setUploadingClientDoc(true);
        try {
            const fd = new FormData();
            fd.append('file', clientDocFile);
            fd.append('title', clientDocTitle.trim());
            fd.append('secteur', clientDocSecteur.trim());
            await clientDocsAPI.create(fd);
            toast.success('Document client ajouté');
            setClientDocTitle(''); setClientDocSecteur(''); setClientDocFile(null);
            const input = document.getElementById('client-doc-file');
            if (input) input.value = '';
            loadClientDocs();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'ajout");
        } finally {
            setUploadingClientDoc(false);
        }
    };

    const handleDownloadClientDoc = async (doc) => {
        setDownloadingClientDocId(doc.id);
        try {
            const res = await clientDocsAPI.download(doc.id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.original_filename || doc.title || 'document';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erreur lors du téléchargement');
        } finally {
            setDownloadingClientDocId(null);
        }
    };

    const handleViewClientDoc = async (doc) => {
        setViewingClientDocId(doc.id);
        try {
            const res = await clientDocsAPI.download(doc.id);
            const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            // Réutilise la Dialog de preview partagée
            setPreviewDoc(doc);
            setPreviewUrl(url);
        } catch {
            toast.error("Erreur lors de l'ouverture du document");
        } finally {
            setViewingClientDocId(null);
        }
    };

    const handleDeleteClientDoc = async (doc) => {
        if (!window.confirm(`Supprimer « ${doc.title} » ?`)) return;
        try {
            await clientDocsAPI.remove(doc.id);
            toast.success('Document supprimé');
            setClientDocs((prev) => prev.filter((d) => d.id !== doc.id));
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openEditClientDoc = (doc) => {
        setEditingClientDoc(doc);
        setEditClientTitle(doc.title || '');
        setEditClientSecteur(doc.secteur || '');
        setEditClientFile(null);
    };
    const closeEditClientDoc = () => {
        setEditingClientDoc(null);
        setEditClientTitle('');
        setEditClientSecteur('');
        setEditClientFile(null);
    };
    const handleSaveEditClientDoc = async () => {
        if (!editingClientDoc) return;
        const title = editClientTitle.trim();
        const secteur = editClientSecteur.trim();
        if (!title || !secteur) {
            toast.error('Le titre et le secteur sont obligatoires');
            return;
        }
        setSavingClientEdit(true);
        try {
            const fd = new FormData();
            if (title !== editingClientDoc.title) fd.append('title', title);
            if (secteur !== editingClientDoc.secteur) fd.append('secteur', secteur);
            if (editClientFile) fd.append('file', editClientFile);
            if (![...fd.keys()].length) {
                toast.info('Aucun changement à enregistrer');
                setSavingClientEdit(false);
                return;
            }
            const res = await clientDocsAPI.update(editingClientDoc.id, fd);
            const updated = res.data;
            setClientDocs((prev) => prev.map((d) => (d.id === editingClientDoc.id ? { ...d, ...updated } : d)));
            toast.success('Document mis à jour');
            closeEditClientDoc();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSavingClientEdit(false);
        }
    };

    const openProspectPicker = async () => {
        setProspectQuery('');
        setShowProspectPicker(true);
        if (prospects.length === 0) {
            try {
                const res = await api.get('/prospects?page_size=100000');
                const list = Array.isArray(res.data) ? res.data : (res.data.prospects || []);
                setProspects(list);
            } catch (err) {
                toast.error('Erreur chargement prospects');
            }
        }
    };

    const filteredProspects = (() => {
        if (!prospectQuery.trim()) return prospects.slice(0, 100);
        const q = prospectQuery.toLowerCase().trim();
        const tokens = q.split(/\s+/).filter(Boolean);
        return prospects.filter(p => {
            const hay = [
                p.last_name, p.first_name, p.company_name,
                p.city, p.email, p.phone, p.id?.slice(0, 8),
            ].filter(Boolean).join(' ').toLowerCase();
            return tokens.every(t => hay.includes(t));
        });
    })();

    const handlePickerKeyDown = (e) => {
        if (e.key === 'Enter' && filteredProspects.length > 0 && !openingForm) {
            e.preventDefault();
            handlePickProspect(filteredProspects[0]);
        }
    };

    const handlePickProspect = async (prospect) => {
        setOpeningForm(true);
        try {
            const res = await studyFormsAPI.create(prospect.id, {});
            setShowProspectPicker(false);
            setOpeningForm(false);
            navigate(`/prospects/${prospect.id}/study/${res.data.id}`);
        } catch (err) {
            setOpeningForm(false);
            toast.error(err.response?.data?.detail || 'Erreur création du relevé');
        }
    };

    // 📘 Guide des parrains (onboarding) — Admin/VP uniquement, servi par l'API (jamais d'URL publique)
    const [guideBusy, setGuideBusy] = useState(null);
    const openGuideParrains = async (download) => {
        setGuideBusy(download ? 'download' : 'view');
        try {
            const res = await api.get('/documents/guide-parrains', { params: download ? { download: 1 } : {}, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (download) {
                const a = document.createElement('a');
                a.href = url; a.download = 'Guide_des_parrains_Valnexo.pdf'; document.body.appendChild(a); a.click(); a.remove();
            } else {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error(e?.response?.status === 403 ? 'Document réservé aux administrateurs et aux VP' : 'Impossible d\'ouvrir le guide');
        } finally {
            setGuideBusy(null);
        }
    };

    const openExternal = (url) => {
        activityAPI.toolOpen(url, 'lien_externe').catch(() => {});
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const downloadAsset = async (asset) => {
        activityAPI.toolOpen(asset.url, 'support_commercial').catch(() => {});
        try {
            const res = await fetch(asset.url);
            if (!res.ok) throw new Error('fetch failed');
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = asset.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
            toast.success('Téléchargement lancé');
        } catch (err) {
            toast.error("Téléchargement impossible, ouverture dans un nouvel onglet");
            window.open(asset.url, '_blank', 'noopener,noreferrer');
        }
    };

    const visibleAssets = MARKETING_ASSETS.filter(a => !a.adminOnly || isAdmin || isVP);

    // Documents "fondations" (fil rouge + ciblage) épinglés en haut de page.
    // Ils sont uploadés comme documents utiles normaux mais isolés par un secteur réservé,
    // puis retirés de la liste générale pour éviter les doublons.
    const isFondationsDoc = (d) => (d?.secteur || '').toLowerCase() === FONDATIONS_SECTEUR.toLowerCase();
    const fondationsDocs = usefulDocs.filter(isFondationsDoc);
    const filRougeDoc =
        fondationsDocs.find((d) => (d.title || '').toLowerCase().includes(FIL_ROUGE_TITLE_HINT)) || null;
    const ciblageDoc =
        fondationsDocs.find((d) => (d.title || '').toLowerCase().includes(CIBLAGE_TITLE_HINT)) || null;
    const generalDocs = usefulDocs.filter(d => d.id !== filRougeDoc?.id && d.id !== ciblageDoc?.id);

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="outils-liens-page">
                {docReading.error && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="document-seen-sync-error">
                    <span>La lecture n’a pas pu être enregistrée.</span>
                    <Button variant="outline" size="sm" onClick={docReading.retry} data-testid="document-seen-sync-retry">Réessayer</Button>
                </div>}
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                        <Wrench className="w-7 h-7 text-blue-600" />
                        Outils &amp; liens utiles
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Tout ce dont vous avez besoin au quotidien pour rassurer vos prospects et structurer votre discours
                    </p>
                </div>

                {/* Quick-nav — pour ne rien oublier sur cette page */}
                <div
                    className="rounded-xl border border-slate-200 bg-white/70 backdrop-blur px-3 py-2.5 shadow-sm overflow-x-auto"
                    data-testid="outils-quick-nav"
                >
                    <div className="flex items-center gap-2 flex-nowrap">
                        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider shrink-0 mr-1">
                            Sur cette page :
                        </span>
                        {[
                            { href: 'vision-valnexo-card', label: 'Vision Valnexo', color: 'text-amber-700 bg-amber-50 border-amber-200', star: true },
                            { href: 'useful-docs-body', label: 'Documents utiles', color: 'text-blue-700 bg-blue-50 border-blue-200', star: true },
                            { href: 'tool-section-forms', label: 'Formulaires', color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
                            { href: 'important-banner', label: 'Rappel important', color: 'text-orange-700 bg-orange-50 border-orange-200' },
                            { href: 'announcements-catalog-card', label: 'Guide des nouveautés', color: 'text-purple-700 bg-purple-50 border-purple-200' },
                        ].map(item => (
                            <a
                                key={item.href}
                                href={`#${item.href}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (item.href === 'useful-docs-body') {
                                        setUsefulDocsOpen(true);
                                        requestAnimationFrame(() => document.querySelector('[data-testid="useful-docs-body"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                                        return;
                                    }
                                    const el = document.querySelector(`[data-testid="${item.href}"]`);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition hover:shadow-sm ${item.color}`}
                                data-testid={`outils-quick-nav-${item.href}`}
                            >
                                {item.star && <Sparkles className="w-3 h-3" />}
                                {item.label}
                            </a>
                        ))}
                    </div>
                </div>

                {/* === 🌟 NOUVELLE VISION VALNEXO — visible par TOUS les rôles, en tout premier === */}
                <Card
                    className="relative border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-md overflow-hidden"
                    data-testid="vision-valnexo-card"
                >
                    {(ciblageDoc?.is_unread || filRougeDoc?.is_unread) && <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-md" data-testid="vision-documents-unread">
                        <Sparkles className="w-3.5 h-3.5" />
                        Nouveauté
                    </div>}
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-3 text-lg sm:text-xl font-bold text-slate-900 pr-24">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm shrink-0">
                                <BookOpen className="w-6 h-6 text-white" />
                            </div>
                            <span>La base à maîtriser avant tout — nouvelle vision Valnexo</span>
                        </CardTitle>
                        <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                            Ces <strong>deux documents constituent le socle</strong> à connaître par cœur avant même de
                            parler de Valnexo à un interlocuteur. C&apos;est une <strong>évolution majeure de notre vision</strong>
                            et de la manière dont nous communiquons.
                        </p>
                        <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                            Ne les lisez pas bêtement à haute voix : <strong>apprenez-les, comprenez-les, appropriez-vous
                            les messages clés</strong>, puis retranscrivez-les avec vos propres mots à chacun de vos
                            interlocuteurs. Ils constituent le <em>fil rouge</em> de votre discours.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {/* Doc 1 — Ciblage général (à voir en premier) général */}
                        {ciblageDoc ? (
                            <div
                                className="flex items-start gap-4 p-4 rounded-lg bg-white border-2 border-blue-300 hover:border-blue-500 hover:shadow-md transition-all group"
                                data-testid="vision-doc-ciblage"
                                data-useful-document-id={ciblageDoc.id}
                            >
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                                    <FileText className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">
                                            Ciblage général
                                        </span>
                                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">
                                            PDF
                                        </span>
                                    </div>
                                    <h3 className="text-base font-bold text-slate-900">{ciblageDoc.title || 'Qui cibler chez Valnexo'}</h3>
                                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                                        Le ciblage général, hors opportunités temporaires — mis à jour au fil de nos évolutions.
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                                            onClick={() => handleViewDoc(ciblageDoc)}
                                            disabled={viewingDocId === ciblageDoc.id}
                                            data-testid="vision-doc-ciblage-view"
                                        >
                                            {viewingDocId === ciblageDoc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                            Consulter
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
                                            onClick={() => handleDownloadDoc(ciblageDoc)}
                                            disabled={downloadingDocId === ciblageDoc.id}
                                            data-testid="vision-doc-ciblage-download"
                                        >
                                            {downloadingDocId === ciblageDoc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                            Télécharger
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 rounded-lg bg-slate-50 border border-dashed border-slate-300 text-xs text-slate-500" data-testid="vision-doc-ciblage-missing">
                                Document « Qui cibler chez Valnexo » à venir — un administrateur doit l&apos;ajouter dans la section
                                « Documents utiles à vos prospections » avec le secteur « {FONDATIONS_SECTEUR} ».
                            </div>
                        )}

                        {/* Doc 2 — Fil rouge (le plus important — à approfondir après le ciblage) */}
                        {filRougeDoc ? (
                            <div
                                className="flex items-start gap-4 p-4 rounded-lg bg-white border-2 border-red-300 hover:border-red-500 hover:shadow-md transition-all group"
                                data-testid="vision-doc-fil-rouge"
                                data-useful-document-id={filRougeDoc.id}
                            >
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shrink-0 shadow-sm">
                                    <FileText className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase">
                                            Le plus important
                                        </span>
                                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">
                                            PDF
                                        </span>
                                    </div>
                                    <h3 className="text-base font-bold text-slate-900">{filRougeDoc.title || 'Fil rouge du discours Valnexo'}</h3>
                                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                                        Les messages clés impactants à maîtriser et transmettre à chaque interlocuteur.
                                        À apprendre et à faire vôtre — pas à réciter.
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                                            onClick={() => handleViewDoc(filRougeDoc)}
                                            disabled={viewingDocId === filRougeDoc.id}
                                            data-testid="vision-doc-fil-rouge-view"
                                        >
                                            {viewingDocId === filRougeDoc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                            Consulter
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="gap-1.5 bg-red-600 hover:bg-red-700"
                                            onClick={() => handleDownloadDoc(filRougeDoc)}
                                            disabled={downloadingDocId === filRougeDoc.id}
                                            data-testid="vision-doc-fil-rouge-download"
                                        >
                                            {downloadingDocId === filRougeDoc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                            Télécharger
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 rounded-lg bg-slate-50 border border-dashed border-slate-300 text-xs text-slate-500" data-testid="vision-doc-fil-rouge-missing">
                                Document « Fil rouge du discours Valnexo » à venir — un administrateur doit l&apos;ajouter dans la section
                                « Documents utiles à vos prospections » avec le secteur « {FONDATIONS_SECTEUR} ».
                            </div>
                        )}

                        {/* Formulaire de qualification — explication liée */}
                        <div className="mt-4 p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200" data-testid="vision-qualif-explain">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-sm">
                                    <ClipboardList className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-base font-bold text-slate-900 mb-1">
                                        Nouveau : formulaire de qualification énergétique
                                    </h3>
                                    <p className="text-sm text-slate-700 leading-relaxed">
                                        Sur chaque fiche prospect (juste au-dessus de Géoportail), vous trouverez un
                                        <strong> formulaire de qualification énergétique</strong> non bloquant, à remplir
                                        après un rendez-vous. Chacun aura le sien — pas de choix à faire.
                                    </p>
                                    <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                                        Ce formulaire génère automatiquement une <strong>note sur 4</strong> et des
                                        <strong> recommandations</strong> exploitables. C&apos;est une <em>première
                                        estimation</em> — la rentabilité définitive reste confirmée par l&apos;étude
                                        technique du bureau d&apos;études.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer — évolution + Académie */}
                        <div className="mt-4 pt-4 border-t border-amber-200/70">
                            <p className="text-xs text-slate-600 italic leading-relaxed text-center">
                                Nous évoluons pour être plus impactants dans nos échanges et plus efficaces sur le
                                terrain. <strong>Une nouvelle vidéo dédiée à cette nouvelle vision arrivera très
                                prochainement dans l&apos;Académie</strong> — restez à l&apos;écoute.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* === SECTION 6 — Documents utiles à vos prospections ===
                    Design pédagogique + collapsible : n'occupe pas toute la place tant que
                    l'utilisateur ne l'a pas ouverte. En tête ambitieuse pour donner envie. */}
                <Card
                    className="border-teal-200 bg-gradient-to-br from-teal-50 via-white to-emerald-50 overflow-hidden"
                    data-testid="tool-section-useful-docs"
                >
                    <CardHeader
                        className={usefulDocsOpen ? "pb-2 cursor-pointer select-none" : "pb-4 cursor-pointer select-none"}
                        onClick={() => setUsefulDocsOpen((v) => !v)}
                        data-testid="useful-docs-toggle"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-900">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-sm">
                                        <BookOpen className="w-5 h-5 text-white" />
                                    </div>
                                    <span>Documents utiles à vos prospections</span>
                                    {generalDocs.length > 0 && (
                                        <span className="ml-1 inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-teal-600 text-white text-xs font-bold" data-testid="useful-docs-total-count" title="Nombre total de documents">
                                            {generalDocs.length}
                                        </span>
                                    )}
                                    {generalDocs.some(doc => doc.is_unread) && <span className="text-xs font-medium text-red-700" data-testid="useful-docs-new-count">{generalDocs.filter(doc => doc.is_unread).length} non vu(s)</span>}
                                </CardTitle>
                                <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                                    <Rocket className="inline w-4 h-4 text-teal-600 mr-1 -mt-0.5" />
                                    <strong>Tous les process pour être 100 % autonome sur vos projets.</strong>
                                    {' '}Fiches à jour, argumentaires, méthodes de closing —
                                    les meilleures opportunités à l&apos;instant T pour transformer vos rendez-vous.
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                                <Button
                                    variant={usefulDocsOpen ? "outline" : "default"}
                                    size="sm"
                                    className={usefulDocsOpen ? "" : "bg-teal-600 hover:bg-teal-700"}
                                    onClick={(e) => { e.stopPropagation(); setUsefulDocsOpen((v) => !v); }}
                                    data-testid="useful-docs-explore-btn"
                                >
                                    {usefulDocsOpen ? (
                                        <>
                                            <ChevronUp className="w-4 h-4 mr-1" /> Replier
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-4 h-4 mr-1" /> Explorer
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    {usefulDocsOpen && (
                    <CardContent className="p-5 sm:p-6 pt-0" data-testid="useful-docs-body">
                        {/* Formulaire d'ajout (admin) */}
                        {isAdmin && (
                            <div className="mb-5 rounded-xl border border-teal-200 bg-white p-4" data-testid="useful-doc-upload-form">
                                <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                    <Upload className="w-4 h-4 text-teal-600" /> Ajouter un document
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Input
                                        placeholder="Titre du document *"
                                        value={docTitle}
                                        onChange={(e) => setDocTitle(e.target.value)}
                                        data-testid="useful-doc-title"
                                    />
                                    <Input
                                        placeholder="Secteur (ex : Agriculture, Industrie…) *"
                                        value={docSecteur}
                                        onChange={(e) => setDocSecteur(e.target.value)}
                                        data-testid="useful-doc-secteur"
                                    />
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <input
                                        id="useful-doc-file"
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                        onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                                        className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-100 file:px-3 file:py-1.5 file:text-teal-700 file:font-medium"
                                        data-testid="useful-doc-file"
                                    />
                                    <Button
                                        onClick={handleUploadDoc}
                                        disabled={uploadingDoc}
                                        className="gap-2 bg-teal-600 hover:bg-teal-700 sm:ml-auto"
                                        data-testid="useful-doc-submit"
                                    >
                                        {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        Téléverser
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Liste des documents */}
                        {loadingDocs ? (
                            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                                <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                            </div>
                        ) : generalDocs.length === 0 ? (
                            <div className="text-center py-8 text-slate-400" data-testid="useful-docs-empty">
                                <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Aucun document pour le moment.</p>
                            </div>
                        ) : (() => {
                            const q = docSearch.trim().toLowerCase();
                            const filtered = q
                                ? generalDocs.filter((d) =>
                                    (d.title || '').toLowerCase().includes(q) ||
                                    (d.secteur || '').toLowerCase().includes(q))
                                : generalDocs;
                            return (
                            <>
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={docSearch}
                                        onChange={(e) => setDocSearch(e.target.value)}
                                        placeholder="Rechercher par titre ou secteur…"
                                        className="pl-9"
                                        data-testid="useful-docs-search"
                                    />
                                </div>
                                {filtered.length === 0 ? (
                                    <p className="text-sm text-slate-400 text-center py-6" data-testid="useful-docs-no-result">
                                        Aucun document ne correspond à « {docSearch} ».
                                    </p>
                                ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="useful-docs-list">
                                    {filtered.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:shadow-sm transition-shadow"
                                        data-testid={`useful-doc-${doc.id}`}
                                        data-useful-document-id={doc.id}
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                                            <FileText className="w-5 h-5 text-teal-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-900 truncate">{doc.title}</p>
                                            {doc.is_unread && <span className="text-[11px] font-semibold text-red-700" data-testid={`useful-doc-unread-${doc.id}`}>Nouveau</span>}
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 bg-teal-50 rounded-full px-2 py-0.5 mt-1">
                                                <Tag className="w-3 h-3" /> {doc.secteur}
                                            </span>
                                            <p className="text-[11px] text-slate-400 mt-1 truncate">{doc.original_filename}</p>
                                        </div>
                                        <div className="flex flex-col gap-1 shrink-0">
                                            <Button
                                                onClick={() => handleViewDoc(doc)}
                                                disabled={viewingDocId === doc.id}
                                                size="sm"
                                                variant="outline"
                                                className="gap-1.5 text-teal-700 border-teal-200 hover:bg-teal-50"
                                                title="Visionner"
                                                data-testid={`useful-doc-view-${doc.id}`}
                                            >
                                                {viewingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                            <Button
                                                onClick={() => handleDownloadDoc(doc)}
                                                disabled={downloadingDocId === doc.id}
                                                size="sm"
                                                className="gap-1.5 bg-teal-600 hover:bg-teal-700"
                                                title="Télécharger"
                                                data-testid={`useful-doc-download-${doc.id}`}
                                            >
                                                {downloadingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                            </Button>
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        onClick={() => openEditDoc(doc)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100"
                                                        title="Modifier ce document"
                                                        data-testid={`useful-doc-edit-${doc.id}`}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDeleteDoc(doc)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50"
                                                        data-testid={`useful-doc-delete-${doc.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    ))}
                                </div>
                                )}
                            </>
                            );
                        })()}
                    </CardContent>
                    )}
                </Card>

                {/* === SECTION 6-BIS — Documents de présentation CLIENT ===
                    Fév 2026 · miroir de Section 6 mais orienté externe : documents
                    à présenter/envoyer au client en RDV ou en suivi. Même UX,
                    mêmes règles (admin upload, tous voient/téléchargent), couleurs
                    ambre/orange pour distinguer visuellement des docs internes. */}
                <Card
                    className="relative border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 overflow-hidden"
                    data-testid="tool-section-client-docs"
                >
                    {/* Badge NOUVEAU — clairement visible tant que l'utilisateur
                       n'a pas ouvert la section au moins une fois */}
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold shadow-md" data-testid="client-docs-new-badge">
                        <Sparkles className="w-3.5 h-3.5" />
                        NOUVEAU
                    </div>
                    <CardHeader
                        className={clientDocsOpen ? "pb-2 cursor-pointer select-none pr-24" : "pb-4 cursor-pointer select-none pr-24"}
                        onClick={() => setClientDocsOpen((v) => !v)}
                        data-testid="client-docs-toggle"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-slate-900">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                                        <Presentation className="w-5 h-5 text-white" />
                                    </div>
                                    <span>Documents de présentation client</span>
                                    {clientDocs.length > 0 && (
                                        <span className="ml-1 inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-amber-600 text-white text-xs font-bold">
                                            {clientDocs.length}
                                        </span>
                                    )}
                                </CardTitle>
                                <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                                    <Handshake className="inline w-4 h-4 text-amber-600 mr-1 -mt-0.5" />
                                    <strong>Les supports à présenter au client — homologues visuels des documents de prospection.</strong>
                                    {' '}Plaquettes commerciales, présentations produit, exemples de rendus,
                                    tout ce qui vous aide à conclure en RDV.
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                                <Button
                                    variant={clientDocsOpen ? "outline" : "default"}
                                    size="sm"
                                    className={clientDocsOpen ? "" : "bg-amber-600 hover:bg-amber-700"}
                                    onClick={(e) => { e.stopPropagation(); setClientDocsOpen((v) => !v); }}
                                    data-testid="client-docs-explore-btn"
                                >
                                    {clientDocsOpen ? (
                                        <>
                                            <ChevronUp className="w-4 h-4 mr-1" /> Replier
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-4 h-4 mr-1" /> Explorer
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    {clientDocsOpen && (
                    <CardContent className="p-5 sm:p-6 pt-0" data-testid="client-docs-body">
                        {/* Bannière de présentation — explique le POURQUOI de la section */}
                        <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50 border-2 border-amber-300" data-testid="client-docs-intro-banner">
                            <div className="flex items-start gap-3">
                                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0 shadow-sm">
                                    <Handshake className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2 flex-wrap">
                                        Nouveau : les supports à présenter au client
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-bold">
                                            <Sparkles className="w-3 h-3" /> NOUVEAU
                                        </span>
                                    </h3>
                                    <p className="text-sm text-slate-700 leading-relaxed">
                                        Chaque document de la section « <strong>Documents utiles à vos prospections</strong> » ci-dessus
                                        possède désormais son <strong>homologue à présenter au client</strong> : plaquette
                                        commerciale, présentation produit, exemples de rendus, cas clients, argumentaire à laisser en main propre après un rendez-vous.
                                    </p>
                                    <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                                        <strong>Objectif</strong> : arriver en RDV avec les bons visuels, avoir un support à
                                        envoyer par mail après un échange, et présenter Valnexo avec la même qualité que
                                        vous le vivez en interne. <em>Recherche par titre ou secteur pour retrouver le bon
                                        document en 2 clics.</em>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Formulaire d'ajout (admin) */}
                        {isAdmin && (
                            <div className="mb-5 rounded-xl border border-amber-200 bg-white p-4" data-testid="client-doc-upload-form">
                                <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                    <Upload className="w-4 h-4 text-amber-600" /> Ajouter un document client
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Input
                                        placeholder="Titre du document *"
                                        value={clientDocTitle}
                                        onChange={(e) => setClientDocTitle(e.target.value)}
                                        data-testid="client-doc-title"
                                    />
                                    <Input
                                        placeholder="Secteur (ex : Agriculture, Industrie…) *"
                                        value={clientDocSecteur}
                                        onChange={(e) => setClientDocSecteur(e.target.value)}
                                        data-testid="client-doc-secteur"
                                    />
                                </div>
                                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <input
                                        id="client-doc-file"
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                        onChange={(e) => setClientDocFile(e.target.files?.[0] || null)}
                                        className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-amber-100 file:px-3 file:py-1.5 file:text-amber-700 file:font-medium"
                                        data-testid="client-doc-file"
                                    />
                                    <Button
                                        onClick={handleUploadClientDoc}
                                        disabled={uploadingClientDoc}
                                        className="gap-2 bg-amber-600 hover:bg-amber-700 sm:ml-auto"
                                        data-testid="client-doc-submit"
                                    >
                                        {uploadingClientDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                        Téléverser
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Liste des documents */}
                        {loadingClientDocs ? (
                            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                                <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                            </div>
                        ) : clientDocs.length === 0 ? (
                            <div className="text-center py-8 text-slate-400" data-testid="client-docs-empty">
                                <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Aucun document client pour le moment.</p>
                            </div>
                        ) : (() => {
                            const q = clientDocSearch.trim().toLowerCase();
                            const filtered = q
                                ? clientDocs.filter((d) =>
                                    (d.title || '').toLowerCase().includes(q) ||
                                    (d.secteur || '').toLowerCase().includes(q))
                                : clientDocs;
                            return (
                            <>
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={clientDocSearch}
                                        onChange={(e) => setClientDocSearch(e.target.value)}
                                        placeholder="Rechercher par titre ou secteur…"
                                        className="pl-9"
                                        data-testid="client-docs-search"
                                    />
                                </div>
                                {filtered.length === 0 ? (
                                    <p className="text-sm text-slate-400 text-center py-6" data-testid="client-docs-no-result">
                                        Aucun document ne correspond à « {clientDocSearch} ».
                                    </p>
                                ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="client-docs-list">
                                    {filtered.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:shadow-sm transition-shadow"
                                        data-testid={`client-doc-${doc.id}`}
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                                            <FileText className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-900 truncate">{doc.title}</p>
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 mt-1">
                                                <Tag className="w-3 h-3" /> {doc.secteur}
                                            </span>
                                            <p className="text-[11px] text-slate-400 mt-1 truncate">{doc.original_filename}</p>
                                        </div>
                                        <div className="flex flex-col gap-1 shrink-0">
                                            <Button
                                                onClick={() => handleViewClientDoc(doc)}
                                                disabled={viewingClientDocId === doc.id}
                                                size="sm"
                                                variant="outline"
                                                className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                                                title="Visionner"
                                                data-testid={`client-doc-view-${doc.id}`}
                                            >
                                                {viewingClientDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                            <Button
                                                onClick={() => handleDownloadClientDoc(doc)}
                                                disabled={downloadingClientDocId === doc.id}
                                                size="sm"
                                                className="gap-1.5 bg-amber-600 hover:bg-amber-700"
                                                title="Télécharger"
                                                data-testid={`client-doc-download-${doc.id}`}
                                            >
                                                {downloadingClientDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                            </Button>
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        onClick={() => openEditClientDoc(doc)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100"
                                                        title="Modifier ce document"
                                                        data-testid={`client-doc-edit-${doc.id}`}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDeleteClientDoc(doc)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50"
                                                        data-testid={`client-doc-delete-${doc.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    ))}
                                </div>
                                )}
                            </>
                            );
                        })()}
                    </CardContent>
                    )}
                </Card>

                {/* === ORGANIGRAMME VALNEXO (VP + Admin uniquement) === */}
                {(isVP || isAdmin) && (
                    <Card
                        className="relative border-2 border-indigo-300 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 shadow-lg overflow-hidden"
                        data-testid="organigramme-card"
                    >
                        {/* Bannière "Nouveau" */}
                        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold shadow-md" data-testid="organigramme-new-badge">
                            <Sparkles className="w-3.5 h-3.5" />
                            NOUVEAU
                        </div>
                        <CardContent className="p-5 sm:p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center">
                                    <Network className="w-5 h-5 text-indigo-200" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                        Organigramme de Valnexo
                                    </h2>
                                    <p className="text-sm text-indigo-100/80 mt-0.5">
                                        En cas de question, en fonction du sujet, vous saurez à qui vous adresser.
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowOrgaLightbox(true)}
                                className="group relative w-full rounded-xl overflow-hidden border border-indigo-400/30 bg-slate-950/50 hover:border-indigo-300 transition-all block"
                                data-testid="organigramme-preview-btn"
                                aria-label="Agrandir l'organigramme"
                            >
                                {/* Chrome-safe : width/height intrinsèques + display block + aspect-ratio explicite
                                    pour éviter la collision "img inside button with lazy" qui collapsait la hauteur à 0 */}
                                <img
                                    src={ORGANIGRAMME_URL}
                                    alt="Organigramme Valnexo"
                                    width={1672}
                                    height={941}
                                    className="block w-full h-auto object-contain"
                                    style={{ aspectRatio: '1672 / 941' }}
                                    decoding="async"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-slate-900 text-xs font-semibold shadow">
                                        <Maximize2 className="w-3.5 h-3.5" /> Agrandir
                                    </div>
                                </div>
                            </button>

                            {/* Grille : envoyer une demande officielle par secteur */}
                            {orgaConfig && (orgaConfig.subject_areas || []).length > 0 && (
                                <div className="mt-5 pt-5 border-t border-indigo-500/20" data-testid="orga-sectors-grid">
                                    <button
                                        type="button"
                                        onClick={() => setShowOfficialRequestGrid((v) => !v)}
                                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-indigo-400/30 bg-slate-900/60 hover:bg-indigo-600/20 hover:border-indigo-300 transition-colors text-white text-sm font-semibold"
                                        data-testid="orga-toggle-official-request-grid"
                                    >
                                        <span className="flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-indigo-200" />
                                            Faire une demande officielle à un dirigeant
                                        </span>
                                        <span className={`text-indigo-200 transition-transform ${showOfficialRequestGrid ? 'rotate-180' : ''}`}>
                                            ▾
                                        </span>
                                    </button>
                                    {showOfficialRequestGrid && (
                                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {orgaConfig.subject_areas.map((a) => {
                                                const recipients = (a.recipients || [])
                                                    .map((k) => orgaConfig.dirigeants?.[k]?.name || k)
                                                    .join(' / ');
                                                return (
                                                    <button
                                                        key={a.key}
                                                        type="button"
                                                        onClick={() => navigate(`/demandes-officielles?area=${encodeURIComponent(a.key)}`)}
                                                        className="group text-left rounded-lg border border-indigo-400/30 bg-slate-900/60 hover:bg-indigo-600/20 hover:border-indigo-300 px-3 py-2.5 transition-colors"
                                                        data-testid={`orga-sector-btn-${a.key}`}
                                                    >
                                                        <div className="text-sm font-medium text-white flex items-center justify-between gap-2">
                                                            <span className="truncate">{a.label}</span>
                                                            <Mail className="w-3.5 h-3.5 text-indigo-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </div>
                                                        <div className="text-[11px] text-indigo-200/70 truncate mt-0.5">
                                                            → {recipients}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Lightbox organigramme */}
                <Dialog open={showOrgaLightbox} onOpenChange={setShowOrgaLightbox}>
                    <DialogContent className="max-w-6xl w-[95vw] p-2 sm:p-4 bg-slate-950 border-indigo-500/30" data-testid="organigramme-lightbox">
                        <DialogHeader className="px-2 pt-1">
                            <DialogTitle className="text-white">Organigramme de Valnexo</DialogTitle>
                            <DialogDescription className="text-indigo-200/70">
                                Cliquez en dehors ou sur ✕ pour fermer.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="w-full max-h-[80vh] overflow-auto">
                            <img
                                src={ORGANIGRAMME_URL}
                                alt="Organigramme Valnexo (agrandi)"
                                width={1672}
                                height={941}
                                className="block w-full h-auto object-contain"
                                style={{ aspectRatio: '1672 / 941' }}
                                decoding="async"
                            />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* === BANNER IMPORTANT (masqué pour parrain — messaging apporteur/VP/admin) === */}
                {!isParrain && (
                <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 shadow-sm" data-testid="important-banner">
                    <CardContent className="p-5 sm:p-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                                </div>
                            </div>
                            <div className="flex-1 space-y-2">
                                <h2 className="text-base font-bold text-amber-900 tracking-wide uppercase">
                                    Important
                                </h2>
                                <p className="text-sm text-slate-800 leading-relaxed">
                                    Ces outils ne sont pas là pour faire joli.
                                </p>
                                <ul className="text-sm text-slate-800 space-y-1 pl-1">
                                    <li>→ Ils donnent une ligne directrice claire</li>
                                    <li>→ Ils rassurent les prospects</li>
                                    <li>→ Ils structurent votre discours</li>
                                </ul>
                                <p className="text-sm text-slate-800 leading-relaxed pt-2">
                                    À chaque premier contact :<br />
                                    → Vous êtes jugés<br />
                                    → Votre sérieux est évalué immédiatement
                                </p>
                                <p className="text-sm text-slate-800 leading-relaxed pt-1">
                                    → <span className="text-emerald-700 font-semibold">Bonne utilisation</span> = hausse du taux de transformation<br />
                                    → <span className="text-rose-700 font-semibold">Mauvaise utilisation</span> = perte de crédibilité + perte de business
                                </p>
                                <div className="pt-2 mt-2 border-t border-amber-200">
                                    <p className="text-sm text-slate-900 font-semibold">Conclusion :</p>
                                    <p className="text-sm text-slate-800 leading-relaxed">
                                        Vous maîtrisez → vous utilisez → vous imposez<br />
                                        <span className="text-slate-600">Sinon, vous freinez votre propre performance</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                )}

                {/* === SECTION 1 — Formulaires (masqué pour parrain) === */}
                {!isParrain && (
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 overflow-hidden" data-testid="tool-section-forms">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <ClipboardList className="w-5 h-5 text-blue-600" />
                                    <h2 className="text-lg font-bold text-slate-900">
                                        Démarrer un nouveau formulaire / relevé d'étude sur place
                                    </h2>
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    Que ce soit pour un client <strong>CEE Tertiaire classique</strong> ou pour le dispositif de
                                    <strong> Rénovation Globale d'Ampleur</strong> (appartement et/ou immeuble collectif),
                                    cliquez sur le bouton, sélectionnez le prospect concerné, choisissez le bon formulaire dans la
                                    plateforme externe puis commencez votre étude.
                                </p>
                                <p className="text-xs text-slate-500 mt-3">
                                    Le formulaire s'ouvre dans un nouvel onglet. À la soumission, il revient automatiquement dans
                                    votre CRM avec toutes les photos et le PDF récapitulatif.
                                </p>
                            </div>
                            <div className="flex-shrink-0 flex flex-col gap-2">
                                <Button
                                    size="lg"
                                    onClick={openProspectPicker}
                                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg gap-2"
                                    data-testid="open-form-btn"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                    Nouveau formulaire
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/forms-cee')}
                                    className="text-blue-700 hover:bg-blue-100 gap-1"
                                    data-testid="open-form-history-btn"
                                >
                                    <History className="w-4 h-4" />
                                    Voir l'historique
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                )}

                {/* === SECTION 2 — Site internet === */}
                <Card data-testid="tool-section-site">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                                    <Globe className="w-7 h-7 text-emerald-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">Site internet Valnexo</h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    La référence officielle à partager avec vos prospects pour découvrir l'écosystème complet Valnexo.
                                </p>
                                <p className="text-xs text-slate-400 mt-1 break-all">{VALNEXO_SITE}</p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button onClick={() => openExternal(VALNEXO_SITE)} variant="outline" className="gap-2" data-testid="open-site-btn">
                                    <ExternalLink className="w-4 h-4" />
                                    Ouvrir le site
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === Guide des parrains (onboarding) — Admin + VP uniquement, jamais les apporteurs === */}
                {(isAdmin || isVP) && (
                    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="tool-section-guide-parrains">
                        <CardContent className="p-5 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center">
                                        <BookOpen className="w-7 h-7 text-indigo-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-lg font-bold text-slate-900">Guide des parrains — onboarding</h3>
                                        <Badge className="bg-indigo-600 text-white text-[10px]">Admin & VP</Badge>
                                    </div>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Lorsque vous créez un parrain, il reçoit <strong>automatiquement un e-mail de bienvenue avec ses identifiants de connexion et ce guide en pièce jointe</strong>. Dites bien à vos nouveaux parrains de le lire attentivement, puis guidez-les vers la performance : briefing, puis accompagnement dans la durée.
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">PDF · 8 pages · réservé aux VP et administrateurs · consultez-le pour le connaître et le renvoyer si besoin</p>
                                </div>
                                <div className="flex-shrink-0 flex gap-2">
                                    <Button onClick={() => openGuideParrains(false)} variant="outline" className="gap-2" disabled={!!guideBusy} data-testid="guide-parrains-view-btn">
                                        {guideBusy === 'view' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                        Consulter
                                    </Button>
                                    <Button onClick={() => openGuideParrains(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700" disabled={!!guideBusy} data-testid="guide-parrains-download-btn">
                                        {guideBusy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                        Télécharger
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* === Lien Candidature « Rejoindre Valnexo » — ÉTAPE 1 (Admin + VP uniquement) === */}
                {(isAdmin || isVP) && (
                    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="tool-section-rejoindre">
                        <CardContent className="p-5 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                                        <ClipboardCheck className="w-7 h-7 text-emerald-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">ÉTAPE 1</span>
                                        <h3 className="text-lg font-bold text-slate-900">Formulaire de candidature — « Rejoindre Valnexo »</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                                        C'est la <strong>toute première étape</strong> du parcours. Toute nouvelle personne qui souhaite
                                        nous rejoindre doit remplir ce formulaire : <strong>sa candidature ne devient officielle qu'une fois
                                        ce formulaire complété</strong>.
                                    </p>
                                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                        <AlertTriangle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-emerald-800">
                                            Une fois la candidature reçue et <strong>validée par vous et la direction</strong>, vous pourrez
                                            alors transmettre le <strong>lien d'onboarding (étape 2)</strong> ci-dessous.
                                        </p>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 break-all">{REJOINDRE_LINK}</p>
                                </div>
                                <div className="flex-shrink-0 flex flex-col gap-2">
                                    <Button onClick={() => openExternal(REJOINDRE_LINK)} variant="outline" className="gap-2" data-testid="open-rejoindre-link-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        Ouvrir
                                    </Button>
                                    <Button
                                        onClick={() => { navigator.clipboard.writeText(REJOINDRE_LINK); toast.success('Lien copié !'); }}
                                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                                        data-testid="copy-rejoindre-link-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                        Copier le lien
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* === Lien Onboarding nouveaux apporteurs (Admin + VP uniquement) === */}
                {(isAdmin || isVP) && (
                    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="tool-section-onboarding">
                        <CardContent className="p-5 sm:p-7">
                            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                                        <UserPlus className="w-7 h-7 text-purple-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center text-[11px] font-bold text-purple-700 bg-purple-100 rounded-full px-2 py-0.5">ÉTAPE 2</span>
                                        <h3 className="text-lg font-bold text-slate-900">Lien d'onboarding — nouveaux apporteurs</h3>
                                    </div>
                                    <p className="text-sm text-slate-600 mt-2">
                                        Page d'inscription à la formation pour vos futurs apporteurs.
                                    </p>
                                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800">
                                            À ne transmettre <strong>uniquement</strong> que lorsque le postulant a rempli le formulaire
                                            « Rejoindre Valnexo » et qu'il a été validé par <strong>vous et la direction</strong>.
                                        </p>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 break-all">{ONBOARDING_LINK}</p>
                                </div>
                                <div className="flex-shrink-0 flex flex-col gap-2">
                                    <Button onClick={() => openExternal(ONBOARDING_LINK)} variant="outline" className="gap-2" data-testid="open-onboarding-link-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        Ouvrir
                                    </Button>
                                    <Button
                                        onClick={() => { navigator.clipboard.writeText(ONBOARDING_LINK); toast.success('Lien copié !'); }}
                                        className="gap-2 bg-purple-600 hover:bg-purple-700"
                                        data-testid="copy-onboarding-link-btn"
                                    >
                                        <Copy className="w-4 h-4" />
                                        Copier le lien
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* === SECTION 3 — Plaquette === */}
                <Card data-testid="tool-section-plaquette">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                                    <FileText className="w-7 h-7 text-purple-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">Plaquette de présentation</h3>
                                <p className="text-sm text-slate-600 mt-1">
                                    Une version courte et synthétique de la home page, idéale à envoyer en suivi de premier
                                    rendez-vous.
                                </p>
                                <p className="text-xs text-slate-400 mt-1 break-all">{VALNEXO_PLAQUETTE}</p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button onClick={() => openExternal(VALNEXO_PLAQUETTE)} variant="outline" className="gap-2" data-testid="open-plaquette-btn">
                                    <ExternalLink className="w-4 h-4" />
                                    Ouvrir la plaquette
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* === SECTION 4 — Cartes de visite (masqué pour parrain — pas de carte perso) === */}
                {!isParrain && (
                <Card data-testid="tool-section-cards">
                    <CardContent className="p-5 sm:p-7">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                            <div className="flex-shrink-0">
                                <div className="w-14 h-14 rounded-xl bg-rose-100 flex items-center justify-center">
                                    <IdCard className="w-7 h-7 text-rose-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">
                                    {businessCard?.kind === 'admin_directory' ? 'Cartes de visite' : 'Ma carte de visite'}
                                </h3>
                                {loadingCard ? (
                                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
                                    </p>
                                ) : !businessCard ? (
                                    <p className="text-sm text-slate-500 mt-1">Information indisponible.</p>
                                ) : businessCard.kind === 'admin_directory' ? (
                                    <>
                                        <p className="text-sm text-slate-600 mt-1">
                                            {isAdmin
                                                ? 'Accédez à l\'annuaire complet des cartes de visite de l\'équipe Valnexo.'
                                                : 'Consultez votre carte ou celles de votre équipe depuis l\'annuaire.'}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1 break-all">{businessCard.url}</p>
                                    </>
                                ) : businessCard.url ? (
                                    <>
                                        <p className="text-sm text-slate-600 mt-1">
                                            Voici votre carte de visite personnelle prête à être partagée avec vos prospects.
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1 break-all">{businessCard.url}</p>
                                        <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed" data-testid="card-fallback-message">
                                            Si votre carte de visite n'apparaît pas, c'est qu'elle n'a pas encore été créée.
                                            Réclamez-la à votre responsable secteur s'il vous plaît, merci !
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-sm text-slate-700 mt-1 bg-amber-50 border border-amber-200 rounded p-3 leading-relaxed" data-testid="card-missing-message">
                                        Votre carte de visite n'est pas encore créée. Réclamez-la à votre responsable secteur s'il vous plaît, merci !
                                    </p>
                                )}
                            </div>
                            {businessCard?.url && !loadingCard && (
                                <div className="flex-shrink-0">
                                    <Button onClick={() => openExternal(businessCard.url)} variant="outline" className="gap-2" data-testid="open-card-btn">
                                        <ExternalLink className="w-4 h-4" />
                                        {businessCard.label || 'Ouvrir'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
                )}

                {/* === SECTION 5 — Supports commerciaux pour vos réseaux === */}
                <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 overflow-hidden" data-testid="tool-section-marketing">
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg font-bold text-slate-900">
                            <Megaphone className="w-5 h-5 text-indigo-600" />
                            Supports commerciaux pour vos réseaux
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                            Visuels prêts à publier sur vos réseaux sociaux. Aperçu, ouverture en grand ou téléchargement direct.
                        </p>
                    </CardHeader>
                    <CardContent className="p-5 sm:p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {visibleAssets.map((asset) => (
                                <div
                                    key={asset.id}
                                    className="flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                                    data-testid={`marketing-asset-${asset.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => openExternal(asset.url)}
                                        className="group relative block w-full bg-slate-100"
                                        title="Ouvrir en grand"
                                        data-testid={`marketing-preview-${asset.id}`}
                                    >
                                        <img
                                            src={asset.url}
                                            alt={`Story ${asset.title}`}
                                            loading="lazy"
                                            className="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                                            <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-xs font-medium text-white bg-black/50 rounded-full px-3 py-1.5">
                                                <ExternalLink className="w-3.5 h-3.5" /> Ouvrir en grand
                                            </span>
                                        </span>
                                        {asset.adminOnly && (
                                            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-100 rounded-full px-2 py-0.5">
                                                Admin / VP
                                            </span>
                                        )}
                                    </button>
                                    <div className="flex flex-col flex-1 p-4">
                                        <h4 className="text-base font-bold text-slate-900">{asset.title}</h4>
                                        <p className="text-xs text-slate-500 mt-1 leading-relaxed flex-1">{asset.description}</p>
                                        <div className="flex gap-2 mt-3">
                                            <Button
                                                onClick={() => openExternal(asset.url)}
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 gap-1.5"
                                                data-testid={`marketing-open-${asset.id}`}
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                Ouvrir
                                            </Button>
                                            <Button
                                                onClick={() => downloadAsset(asset)}
                                                size="sm"
                                                className="flex-1 gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                                                data-testid={`marketing-download-${asset.id}`}
                                            >
                                                <Download className="w-4 h-4" />
                                                Télécharger
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>


                {/* === Modal ÉDITION document (admin) — modifier titre, secteur, remplacer fichier === */}
                <Dialog open={!!editingDoc} onOpenChange={(o) => { if (!o) closeEditDoc(); }}>
                    <DialogContent className="max-w-lg" data-testid="useful-doc-edit-modal">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Edit className="w-5 h-5 text-slate-700" />
                                Modifier ce document
                            </DialogTitle>
                            <DialogDescription>
                                Les modifications sont immédiatement visibles pour tous les utilisateurs.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label>Titre</Label>
                                <Input
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    data-testid="useful-doc-edit-title"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Secteur</Label>
                                <Input
                                    value={editSecteur}
                                    onChange={(e) => setEditSecteur(e.target.value)}
                                    data-testid="useful-doc-edit-secteur"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Remplacer le fichier (facultatif)</Label>
                                <Input
                                    type="file"
                                    onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                    data-testid="useful-doc-edit-file"
                                />
                                {editingDoc?.original_filename && !editFile && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        Fichier actuel : {editingDoc.original_filename}
                                    </p>
                                )}
                                {editFile && (
                                    <p className="text-xs text-emerald-700 mt-1">
                                        Nouveau fichier : {editFile.name}
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeEditDoc} disabled={savingEdit} data-testid="useful-doc-edit-cancel">
                                Annuler
                            </Button>
                            <Button onClick={handleSaveEditDoc} disabled={savingEdit} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="useful-doc-edit-save">
                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit className="w-4 h-4" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* === Modal ÉDITION document CLIENT (admin) — miroir de l'édition des docs utiles === */}
                <Dialog open={!!editingClientDoc} onOpenChange={(o) => { if (!o) closeEditClientDoc(); }}>
                    <DialogContent className="max-w-lg" data-testid="client-doc-edit-modal">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Edit className="w-5 h-5 text-slate-700" />
                                Modifier ce document client
                            </DialogTitle>
                            <DialogDescription>
                                Les modifications sont immédiatement visibles pour tous les utilisateurs.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label>Titre</Label>
                                <Input
                                    value={editClientTitle}
                                    onChange={(e) => setEditClientTitle(e.target.value)}
                                    data-testid="client-doc-edit-title"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Secteur</Label>
                                <Input
                                    value={editClientSecteur}
                                    onChange={(e) => setEditClientSecteur(e.target.value)}
                                    data-testid="client-doc-edit-secteur"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Remplacer le fichier (facultatif)</Label>
                                <Input
                                    type="file"
                                    onChange={(e) => setEditClientFile(e.target.files?.[0] || null)}
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                    data-testid="client-doc-edit-file"
                                />
                                {editingClientDoc?.original_filename && !editClientFile && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        Fichier actuel : {editingClientDoc.original_filename}
                                    </p>
                                )}
                                {editClientFile && (
                                    <p className="text-xs text-emerald-700 mt-1">
                                        Nouveau fichier : {editClientFile.name}
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeEditClientDoc} disabled={savingClientEdit} data-testid="client-doc-edit-cancel">
                                Annuler
                            </Button>
                            <Button onClick={handleSaveEditClientDoc} disabled={savingClientEdit} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="client-doc-edit-save">
                                {savingClientEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit className="w-4 h-4" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* === Modal prévisualisation document — plein écran sur mobile,
                    bouton Retour toujours visible en haut à gauche + X en haut à droite,
                    contenu centré. Fermeture ESC / clic hors modal / bouton Retour. === */}
                <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) closePreview(); }}>
                    <DialogContent
                        className="max-w-6xl w-[98vw] sm:w-[95vw] p-0 overflow-hidden rounded-none sm:rounded-xl h-[100dvh] sm:h-[92vh] sm:max-h-[92vh] flex flex-col"
                        data-testid="useful-doc-preview-modal"
                    >
                        <DialogHeader className="px-3 sm:px-5 py-3 border-b bg-white flex-row items-center gap-3 space-y-0 shrink-0">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={closePreview}
                                className="shrink-0 gap-1 -ml-2"
                                data-testid="useful-doc-preview-back-btn"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="hidden sm:inline">Retour</span>
                            </Button>
                            <div className="flex-1 min-w-0">
                                <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
                                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 shrink-0" />
                                    <span className="truncate">{previewDoc?.title || 'Document'}</span>
                                </DialogTitle>
                                <DialogDescription className="truncate text-xs">
                                    {previewDoc?.secteur ? `${previewDoc.secteur} · ` : ''}{previewDoc?.original_filename}
                                </DialogDescription>
                            </div>
                        </DialogHeader>
                        <div className="bg-slate-100 flex-1 min-h-0 relative">
                            {previewUrl && previewDoc && (
                                (previewDoc.mime_type || '').startsWith('image/') ? (
                                    <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
                                        <img
                                            src={previewUrl}
                                            alt={previewDoc.title}
                                            className="max-w-full max-h-full object-contain mx-auto"
                                            data-testid="useful-doc-preview-image"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-full h-full mx-auto">
                                        <PdfAllPagesViewer url={previewUrl} />
                                    </div>
                                )
                            )}
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-3 border-t bg-white shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={closePreview}
                                className="gap-1 sm:hidden"
                                data-testid="useful-doc-preview-back-btn-mobile"
                            >
                                <ArrowLeft className="w-4 h-4" /> Retour
                            </Button>
                            <div className="hidden sm:block" />
                            {previewDoc && (
                                <Button
                                    onClick={() => handleDownloadDoc(previewDoc)}
                                    className="gap-2 bg-teal-600 hover:bg-teal-700"
                                    data-testid="useful-doc-preview-download"
                                >
                                    <Download className="w-4 h-4" /> Télécharger
                                </Button>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* === Modal prospect picker === */}
                <Dialog open={showProspectPicker} onOpenChange={setShowProspectPicker}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Sélectionner le prospect</DialogTitle>
                            <DialogDescription>
                                Choisissez le prospect auquel rattacher ce formulaire d'étude.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="relative mb-3">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Tapez un nom, société, ville, email..."
                                value={prospectQuery}
                                onChange={(e) => setProspectQuery(e.target.value)}
                                onKeyDown={handlePickerKeyDown}
                                className="pl-9"
                                data-testid="prospect-picker-search"
                                autoFocus
                            />
                            {prospects.length > 0 && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                                    {filteredProspects.length}/{prospects.length}
                                </div>
                            )}
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 border rounded-lg">
                            {prospects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des prospects...
                                </div>
                            ) : filteredProspects.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400">Aucun prospect trouvé</div>
                            ) : (
                                filteredProspects.slice(0, 200).map((p, idx) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => handlePickProspect(p)}
                                        disabled={openingForm}
                                        className={`w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between transition-colors disabled:opacity-50 ${idx === 0 && prospectQuery ? 'bg-blue-50/50' : ''}`}
                                        data-testid={`pick-prospect-${p.id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-900 truncate">
                                                {p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Sans nom'}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                                                {[
                                                    p.company_name && `${p.first_name || ''} ${p.last_name || ''}`.trim(),
                                                    p.city,
                                                    p.email,
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                                    </button>
                                ))
                            )}
                        </div>
                        {prospectQuery && filteredProspects.length > 0 && !openingForm && (
                            <div className="text-xs text-slate-500 mt-2 text-center">
                                Appuyez sur <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border text-[10px] font-mono">Entrée</kbd> pour sélectionner le premier résultat
                            </div>
                        )}
                        {openingForm && (
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> Génération du lien sécurisé...
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* === GUIDE DES NOUVEAUTÉS === Placé en bas pour ne pas amoindrir les 2 sections essentielles */}
                <AnnouncementsCatalog />
            </div>
        </DashboardLayout>
    );
}
